import { useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import SEO from '@/components/base/SEO';
import Breadcrumb from '@/components/base/Breadcrumb';
import ManhwaCard from '@/components/feature/ManhwaCard';
import { manhwas } from '@/mocks/manhwas';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();

  const manhwa = useMemo(() => {
    return manhwas.find((m) => m.id === id) || null;
  }, [id]);

  const relatedManhwas = useMemo(() => {
    if (!manhwa) return [];
    return manhwas
      .filter((m) => m.id !== manhwa.id && m.categorySlug === manhwa.categorySlug)
      .slice(0, 4);
  }, [manhwa]);

  if (!manhwa) {
    return (
      <main className="min-h-screen pt-20 flex items-center justify-center">
        <div className="text-center">
          <i className="ri-emotion-sad-line text-5xl text-foreground-300 block mb-4"></i>
          <h1 className="text-xl font-heading text-foreground-900 mb-2">漫画未找到</h1>
          <p className="text-sm text-foreground-500 mb-6">抱歉，您查找的漫画不存在或已被移除</p>
          <Link
            to="/category/all"
            className="inline-flex items-center gap-2 px-6 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-all duration-200 whitespace-nowrap"
          >
            浏览全部漫画
          </Link>
        </div>
      </main>
    );
  }

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: manhwa.category, href: `/category/${manhwa.categorySlug}` },
    { label: manhwa.title },
  ];

  const schemaData = [
    {
      '@context': 'https://schema.org',
      '@type': 'Book',
      name: manhwa.title,
      author: {
        '@type': 'Person',
        name: manhwa.author,
      },
      description: manhwa.description,
      inLanguage: 'zh-CN',
      datePublished: manhwa.updateDate,
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: manhwa.rating,
        bestRating: '10',
        ratingCount: Math.floor(manhwa.views / 100),
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: breadcrumbItems.map((item, idx) => ({
        '@type': 'ListItem' as const,
        position: idx + 1,
        name: item.label,
        item: item.href ? `https://www.hanmanwu.com${item.href}` : undefined,
      })),
    },
  ];

  return (
    <>
      <SEO
        title={`${manhwa.title}_${manhwa.category}韩国漫画_韩漫屋在线观看`}
        description={`${manhwa.title} - ${manhwa.description.slice(0, 120)}。韩漫屋提供${manhwa.title}高清免费在线观看，${manhwa.status}，共${manhwa.totalChapters}话。`}
        keywords={`${manhwa.title},${manhwa.category}韩国漫画,韩漫屋,免费在线观看,韩漫`}
        canonicalUrl={`https://www.hanmanwu.com/manhwa/${manhwa.id}`}
        ogType="book"
        schemaJsonLd={schemaData}
      />

      <main className="min-h-screen pt-20 pb-10">
        <div className="max-w-7xl mx-auto">
          <Breadcrumb items={breadcrumbItems} />

          <div className="px-4 md:px-6">
            <div className="flex flex-col lg:flex-row gap-8">
              <div className="flex-shrink-0">
                <div className="w-48 md:w-56 aspect-[3/4] rounded-lg overflow-hidden border border-background-200/70">
                  <img
                    src={manhwa.coverImage}
                    alt={manhwa.title}
                    className="w-full h-full object-cover object-top"
                    title={`${manhwa.title} - 好看韩国漫画免费在线观看`}
                  />
                </div>
              </div>

              <div className="flex-1 min-w-0">
                <h1 className="text-2xl md:text-3xl font-heading text-foreground-950 mb-2">
                  {manhwa.title}
                </h1>

                <div className="flex items-center gap-4 flex-wrap mb-4">
                  <span className="text-sm text-foreground-500 flex items-center gap-1">
                    <i className="ri-user-line"></i>
                    {manhwa.author}
                  </span>
                  <Link
                    to={`/category/${manhwa.categorySlug}`}
                    className="text-sm text-primary-500 bg-primary-50 px-3 py-1 rounded-full hover:bg-primary-100 transition-colors duration-200 whitespace-nowrap"
                  >
                    {manhwa.category}
                  </Link>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${
                    manhwa.status === '连载中'
                      ? 'bg-primary-100 text-primary-700'
                      : 'bg-secondary-100 text-secondary-700'
                  }`}>
                    {manhwa.status}
                  </span>
                </div>

                <div className="flex items-center gap-6 mb-6 flex-wrap">
                  <div className="flex items-center gap-1">
                    <i className="ri-star-fill text-accent-500"></i>
                    <span className="text-lg font-bold text-foreground-900">{manhwa.rating}</span>
                    <span className="text-xs text-foreground-500">/ 10</span>
                  </div>
                  <div className="flex items-center gap-1 text-sm text-foreground-500">
                    <i className="ri-eye-line"></i>
                    {manhwa.views > 10000 ? `${(manhwa.views / 10000).toFixed(0)}万` : manhwa.views} 次浏览
                  </div>
                  <div className="flex items-center gap-1 text-sm text-foreground-500">
                    <i className="ri-file-list-3-line"></i>
                    共 {manhwa.totalChapters} 话
                  </div>
                  <div className="flex items-center gap-1 text-sm text-foreground-500">
                    <i className="ri-time-line"></i>
                    更新于 {manhwa.updateDate}
                  </div>
                </div>

                <div className="flex items-center gap-2 mb-6 flex-wrap">
                  {manhwa.tags.map((tag) => (
                    <span
                      key={tag}
                      className="text-xs bg-secondary-100 text-secondary-700 px-2 py-1 rounded-md whitespace-nowrap"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                <div className="bg-background-100 rounded-lg p-5 mb-6">
                  <h2 className="text-sm font-medium text-foreground-900 mb-3">作品简介</h2>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    {manhwa.description}
                  </p>
                </div>

                <div className="flex items-center gap-3">
                  <button className="flex items-center gap-2 px-8 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer">
                    <i className="ri-play-circle-line"></i>
                    开始阅读
                  </button>
                  <button className="flex items-center gap-2 px-6 py-3 rounded-full border border-background-200 text-foreground-600 text-sm font-medium hover:bg-background-100 transition-all duration-200 whitespace-nowrap cursor-pointer">
                    <i className="ri-bookmark-line"></i>
                    收藏
                  </button>
                </div>
              </div>
            </div>

            <div className="mt-12">
              <h2 className="text-lg md:text-xl font-heading text-foreground-950 mb-4">章节目录</h2>
              <div className="bg-background-50 border border-background-200/70 rounded-lg divide-y divide-background-100">
                {manhwa.chapters.map((chapter) => (
                  <div
                    key={chapter.id}
                    className="flex items-center justify-between px-5 py-4 hover:bg-background-50/80 transition-colors duration-200"
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-medium text-foreground-900">
                        {chapter.title}
                      </span>
                      {chapter.number === manhwa.totalChapters && manhwa.status === '连载中' && (
                        <span className="text-[10px] bg-primary-100 text-primary-600 px-1.5 py-0.5 rounded whitespace-nowrap">
                          NEW
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-4 text-xs text-foreground-500">
                      <span>{chapter.updateDate}</span>
                      <span className="flex items-center gap-1">
                        <i className="ri-eye-line"></i>
                        {chapter.views > 10000 ? `${(chapter.views / 10000).toFixed(1)}万` : chapter.views}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
              {manhwa.totalChapters > 4 && (
                <div className="text-center mt-4">
                  <button className="text-sm text-primary-500 hover:text-primary-600 font-medium transition-colors duration-200 cursor-pointer whitespace-nowrap">
                    查看全部 {manhwa.totalChapters} 话 <i className="ri-arrow-down-s-line"></i>
                  </button>
                </div>
              )}
            </div>

            {relatedManhwas.length > 0 && (
              <div className="mt-14">
                <div className="flex items-end justify-between mb-6">
                  <div>
                    <h2 className="text-lg md:text-xl font-heading text-foreground-950">
                      相关推荐
                    </h2>
                    <p className="text-sm text-foreground-500 mt-1">更多{manhwa.category}类漫画</p>
                  </div>
                  <Link
                    to={`/category/${manhwa.categorySlug}`}
                    className="text-sm text-primary-500 hover:text-primary-600 font-medium flex items-center gap-1 transition-colors duration-200 whitespace-nowrap"
                  >
                    查看更多 <i className="ri-arrow-right-line"></i>
                  </Link>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
                  {relatedManhwas.map((m) => (
                    <ManhwaCard key={m.id} manhwa={m} />
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
    </>
  );
}